import { Link } from 'react-router-dom';
import Header from '../components/Header';

const S = ({ n, title, children }) => (
  <section className="mb-10">
    <h2 className="text-lg md:text-lg font-bold text-neutral-900 mb-3">{n}. {title}</h2>
    <div className="space-y-3 text-neutral-600 text-sm md:text-base leading-relaxed">{children}</div>
  </section>
);

const UL = ({ items }) => (
  <ul className="list-disc pl-6 space-y-1.5">
    {items.map((it, i) => <li key={i}>{it}</li>)}
  </ul>
);

export default function TermsPage() {
  return (
    <div className="min-h-screen bg-white">
      <Header />
      <div data-testid="terms-page" className="max-w-3xl mx-auto px-6 pt-28 pb-20">
        <p className="text-xs font-semibold tracking-widest text-[#FF3B30] uppercase mb-3">Spinser Inc. | Legal Policies</p>
        <h1 className="text-4xl sm:text-5xl font-bold text-neutral-900 mb-2">Terms and Conditions</h1>
        <p className="text-neutral-500 mb-1">Platform-wide terms governing tryspinser.com and Spinser campaigns and services.</p>
        <p className="text-sm text-neutral-400 mb-6">Effective July 23, 2026 &middot; Last updated July 23, 2026</p>

        <div className="flex gap-4 mb-10 text-sm">
          <Link to="/privacy" data-testid="terms-to-privacy-link" className="text-[#FF3B30] font-medium hover:underline">Privacy Policy</Link>
          <Link to="/privacy#cookie-policy" className="text-[#FF3B30] font-medium hover:underline">Cookie Policy</Link>
        </div>

        <div className="space-y-3 text-neutral-600 text-sm md:text-base leading-relaxed mb-10">
          <p>These Terms and Conditions ("Terms") govern your access to and use of the websites, applications, portals, promotions and services operated under the Spinser brand, including tryspinser.com (collectively, the "Platform"). The Platform is operated by Spinser Inc. ("Spinser," "we," "us" or "our").</p>
          <p>By accessing the Platform, creating an account, submitting an entry, participating in a SponsoredSpin, using a referral link, registering as a sponsor or service provider, or otherwise using our services, you agree to these Terms and our Privacy Policy.</p>
          <p className="font-semibold text-neutral-800">If you do not agree, do not use the Platform.</p>
        </div>

        <S n={1} title="About Spinser">
          <p>Spinser operates an online marketing, sponsorship and technology platform that connects homeowners and other eligible participants with sponsors, real estate professionals, contractors, home-service providers and participating brands.</p>
          <p>Spinser may organize or facilitate sponsored promotions through which eligible participants can enter for an opportunity to receive home services, home improvements, products, credits or other benefits. These promotions may be referred to as "SponsoredSpins," "Spins," "Campaigns," "Giveaways" or "Promotions."</p>
          <p>Spinser is not a lottery operator, gambling platform, real estate brokerage, lender, insurer or general contractor unless expressly stated in writing for a particular service.</p>
        </S>

        <S n={2} title="Eligibility to Use the Platform">
          <p>You must be at least 18 years old and legally capable of entering into a binding agreement to use the Platform. Individual Campaigns may require participants to:</p>
          <UL items={[
            'Have reached the age of majority in their province, territory or state;',
            'Reside within a particular geographic area;',
            'Own, occupy or have lawful authority over an eligible property;',
            'Satisfy property, identity or address-verification requirements; or',
            'Meet other requirements described in the applicable Official Rules.',
          ]} />
          <p>Employees, representatives, contractors, sponsors and immediate family or household members may be excluded from particular Campaigns. Eligibility for each Campaign is determined by its Official Rules.</p>
        </S>

        <S n={3} title="Campaign Official Rules">
          <p>Each Campaign will be governed by separate Official Rules or campaign terms made available before or when entries are accepted. The Official Rules may include:</p>
          <UL items={[
            'The Campaign sponsor or sponsors;',
            'Campaign start and end dates;',
            'Eligibility and geographic restrictions;',
            'Available methods of entry;',
            'Entry and referral limits;',
            'Prize descriptions and approximate retail values;',
            'The number of available prizes;',
            'Any regional allocation of prizes;',
            'Winner-selection procedures;',
            'Material factors affecting the odds of winning;',
            'Verification requirements;',
            'Canadian skill-testing requirements, when applicable;',
            'Prize-fulfilment conditions; and',
            'Publicity, recording or release requirements.',
          ]} />
          <p>These Terms apply alongside the Official Rules. If these Terms conflict with the Official Rules concerning a particular Campaign, the Official Rules will control for that Campaign.</p>
        </S>

        <S n={4} title="No Purchase Necessary">
          <p>Unless expressly permitted by applicable law and clearly disclosed in the Official Rules, no purchase or payment is required to enter a Spinser Campaign or to win a prize.</p>
          <p>A purchase, payment, sponsorship transaction or decision to use a participating business will not improve an entrant's chances of winning unless expressly permitted by applicable law and clearly stated in the Official Rules.</p>
          <p>Sponsors fund Campaigns for promotional, marketing, community-engagement and lead-generation purposes. Sponsor participation does not require homeowners to purchase services from the sponsor.</p>
        </S>

        <S n={5} title="Entries">
          <p>Entries must be submitted using an authorized entry method identified by Spinser. You agree to provide complete, current and accurate information. Spinser may disqualify or remove entries that:</p>
          <UL items={[
            'Contain false, incomplete or misleading information;',
            'Are submitted outside the Campaign period;',
            'Exceed applicable entry limits;',
            'Use unauthorized scripts, bots, automated systems or fabricated identities;',
            'Manipulate referral, location or verification systems;',
            "Infringe another person's rights;",
            'Violate these Terms or the Official Rules; or',
            'Interfere with the fair administration of a Campaign.',
          ]} />
          <p>Proof that information was submitted does not necessarily constitute proof that Spinser received a valid entry. Spinser's records will govern in the event of a dispute concerning entry receipt, subject to applicable law.</p>
        </S>

        <S n={6} title="Referral and Bonus-Entry Programs">
          <p>Some Campaigns may allow entrants to receive bonus entries by sharing an authorized referral link or completing specified actions. You must not:</p>
          <UL items={[
            'Send unsolicited bulk messages;',
            'Purchase or trade referrals;',
            'Create false accounts or identities;',
            'Misrepresent your relationship with Spinser or a sponsor;',
            "Use misleading statements about a prize or a person's chance of winning;",
            'Post referral links in violation of platform rules;',
            "Share another person's contact information without authorization; or",
            'Use paid advertising containing Spinser trademarks without written permission.',
          ]} />
          <p>Referral entries remain subject to verification and the limits stated in the Official Rules.</p>
        </S>

        <S n={7} title="Winner Selection and Verification">
          <p>Potential winners may be selected randomly, by skill, or through another lawful method described in the Official Rules.</p>
          <p>Being identified as a potential winner does not guarantee that a person will receive a prize. Before being confirmed, a potential winner may be required to:</p>
          <UL items={[
            'Verify identity, age, residence and eligibility;',
            'Verify property ownership, occupancy or authorization;',
            'Correctly answer a mathematical skill-testing question when required;',
            'Sign an eligibility declaration, liability release or prize-acceptance agreement;',
            'Obtain consent from a co-owner, landlord, condominium corporation or other affected person;',
            'Allow a reasonable property assessment;',
            'Provide documentation reasonably necessary to deliver the prize; and',
            'Comply with the Official Rules within the stated deadline.',
          ]} />
          <p>A potential winner may be disqualified if they cannot be contacted, fail verification, decline the prize, provide inaccurate information or do not comply with the required process. Spinser may then select an alternate potential winner.</p>
        </S>

        <S n={8} title="Prizes and Home-Service Projects">
          <p>The exact prize, service scope, maximum value, included materials and exclusions will be described in the Official Rules or a written project agreement. Unless otherwise stated:</p>
          <UL items={[
            'Prizes are not transferable;',
            'No cash substitution is available;',
            'Prizes cannot be exchanged for unrelated services;',
            "Work outside the approved scope is the winner's responsibility;",
            'Hidden damage, hazardous materials, structural defects, code violations and pre-existing conditions are excluded;',
            'Changes requested by the winner may result in additional costs;',
            'Permits, approvals and access requirements may affect scheduling;',
            'Colours, products and materials may be subject to availability; and',
            'The winner must provide safe and reasonable access to the property.',
          ]} />
          <p>Spinser or the applicable sponsor may substitute a prize with another prize of equal or greater approximate value when the original prize cannot reasonably be delivered, subject to applicable law.</p>
          <p>Any applicable taxes, reporting obligations, permits, owner approvals or expenses not expressly included in the prize are the winner's responsibility.</p>
        </S>

        <S n={9} title="Contractors and Service Providers">
          <p>Contractors, tradespeople, real estate professionals, sponsors and other service providers appearing through the Platform are generally independent businesses and are not employees, agents, partners or joint venturers of Spinser.</p>
          <p>Unless expressly stated in writing, Spinser does not control their day-to-day work, professional advice, licensing, warranties, representations or business practices.</p>
          <p>Spinser may conduct screening, collect credentials or establish participation standards, but such steps do not guarantee the quality, legality, safety, availability or suitability of a provider's services.</p>
          <p>Any agreement for work beyond a Campaign prize is between the homeowner and the applicable service provider. Nothing on the Platform constitutes legal, financial, tax, real estate, insurance, construction or professional advice.</p>
        </S>

        <S n={10} title="Sponsors and Business Users">
          <p>Sponsors, real estate professionals, contractors, affiliates and other business users may be subject to separate sponsorship agreements, order forms, campaign agreements or service-provider terms.</p>
          <p>In the event of a conflict, a separately signed agreement will control the commercial relationship between Spinser and that business user.</p>
          <p>Business users must ensure that their advertising, communications, offers, licenses, representations and use of participant information comply with applicable law and their agreement with Spinser.</p>
          <p>Receiving access to participant information does not authorize a business user to send unrestricted marketing communications or use that information for unrelated purposes.</p>
        </S>

        <S n={11} title="Communications and Marketing Consent">
          <p>Spinser may send administrative or transactional communications concerning:</p>
          <UL items={[
            'Entry confirmation;',
            'Campaign status;',
            'Eligibility or verification;',
            'Security or fraud prevention;',
            'Winner selection;',
            'Prize fulfilment;',
            'Account or service updates; and',
            'Changes to our legal terms.',
          ]} />
          <p>Marketing emails and text messages will be sent only where permitted by applicable law and any consent you provide. Consent to marketing is not required to enter a no-purchase Campaign unless the Official Rules clearly state otherwise and applicable law permits it.</p>
          <p>You may unsubscribe from marketing communications using the unsubscribe method provided in the message. Unsubscribing from marketing does not prevent us from sending necessary transactional or Campaign-related messages. Carrier and data charges may apply to text messages.</p>
        </S>

        <S n={12} title="User Content">
          <p>"User Content" includes photographs, videos, reviews, testimonials, comments, project descriptions and other materials you voluntarily upload or submit.</p>
          <p>You retain ownership of your User Content. By submitting it to the Platform, you grant Spinser a worldwide, non-exclusive, royalty-free licence to host, store, reproduce, format and display it for the purpose of operating, securing and promoting the Platform and the applicable Campaign.</p>
          <p>This licence ends when the content is deleted, except where continued retention is reasonably necessary for legal compliance, completed promotional materials, dispute resolution or backup systems.</p>
          <p>You represent that you have the necessary rights and permissions to submit the User Content. Spinser will obtain additional consent or a separate release where required before using a winner's name, likeness, voice, property images, testimonial, reaction video or recorded interview for promotional purposes.</p>
        </S>

        <S n={13} title="Acceptable Use">
          <p>You may not:</p>
          <UL items={[
            'Break any applicable law;',
            'Impersonate another person or organization;',
            'Submit information about another person without authority;',
            'Attempt to gain unauthorized access to the Platform;',
            'Probe, scan or test Platform vulnerabilities without written permission;',
            'Introduce malware, harmful code or disruptive content;',
            'Scrape, copy or extract Platform data through automated means;',
            'Circumvent security, geographic, entry or account restrictions;',
            'Manipulate a Campaign or winner-selection process;',
            'Harass Platform users, winners, sponsors or service providers;',
            'Infringe intellectual-property, privacy or publicity rights; or',
            'Use the Platform to transmit deceptive, discriminatory, defamatory or unlawful content.',
          ]} />
        </S>

        <S n={14} title="Intellectual Property">
          <p>The Platform and its software, designs, text, graphics, logos, trademarks, videos, databases and other content are owned by or licensed to Spinser and are protected by applicable intellectual-property laws.</p>
          <p>"Spinser," "SponsoredSpin," associated logos and related brand elements may not be used without prior written permission.</p>
          <p>These Terms provide a limited, revocable, non-transferable right to use the Platform for its intended purpose. They do not transfer ownership of any Spinser intellectual property.</p>
        </S>

        <S n={15} title="Third-Party Services and Links">
          <p>The Platform may rely on or link to third-party services, including hosting, analytics, mapping, payment, identity-verification, communications, social-media and winner-selection services.</p>
          <p>Spinser is not responsible for third-party websites or services that it does not control. Your use of those services may be governed by separate terms and privacy policies.</p>
        </S>

        <S n={16} title="Platform Availability and Changes">
          <p>Spinser may modify, suspend or discontinue parts of the Platform. We do not guarantee that the Platform will always be uninterrupted, secure or error-free.</p>
          <p>Spinser may modify, suspend, extend or cancel a Campaign where fraud, technical failure, legal requirements, force majeure or circumstances outside reasonable control affect its fairness, security or administration. Any such action will be taken subject to applicable law and the Official Rules.</p>
        </S>

        <S n={17} title="Disclaimers">
          <p>To the fullest extent permitted by law, the Platform is provided on an "as is" and "as available" basis. Spinser disclaims implied warranties of merchantability, fitness for a particular purpose, non-infringement and uninterrupted availability, except where those warranties cannot legally be excluded.</p>
          <p>Spinser does not guarantee:</p>
          <UL items={[
            'That any person will win a Campaign;',
            'A specific number or quality of leads;',
            'A particular business result;',
            'The conduct or performance of a third party;',
            'That every property or requested project will be eligible;',
            'That a project will increase property value; or',
            'That Platform information is free from every error.',
          ]} />
          <p>Nothing in these Terms excludes rights or warranties that cannot legally be excluded.</p>
        </S>

        <S n={18} title="Limitation of Liability">
          <p>To the fullest extent permitted by law, Spinser and its directors, officers, employees and agents will not be liable for indirect, incidental, special, consequential, exemplary or punitive damages, including lost profits, lost opportunities, data loss or reputational harm arising from the Platform or a Campaign.</p>
          <p>To the fullest extent permitted by law, Spinser's aggregate liability arising from the Platform will not exceed the greater of:</p>
          <UL items={[
            'The amount you paid directly to Spinser for the service giving rise to the claim during the preceding 12 months; or',
            'One hundred Canadian dollars.',
          ]} />
          <p>These limitations do not apply to liability that cannot legally be limited, including liability arising from fraud, wilful misconduct or other matters excluded by applicable law.</p>
        </S>

        <S n={19} title="Indemnification">
          <p>To the extent permitted by law, you agree to indemnify and hold harmless Spinser and its directors, officers, employees and agents from third-party claims arising from:</p>
          <UL items={[
            'Your unlawful use of the Platform;',
            'Your breach of these Terms;',
            'Your User Content;',
            "Your infringement of another person's rights; or",
            'Fraudulent or unauthorized Campaign activity attributable to you.',
          ]} />
          <p>This section does not require a consumer to indemnify Spinser for Spinser's own negligence or misconduct.</p>
        </S>

        <S n={20} title="Suspension and Termination">
          <p>Spinser may suspend or terminate access, cancel entries or restrict participation where we reasonably believe a person has:</p>
          <UL items={[
            'Violated these Terms or Official Rules;',
            'Created a security, legal or fraud risk;',
            'Abused the Platform;',
            'Interfered with another user; or',
            'Provided false eligibility information.',
          ]} />
          <p>Where reasonable, Spinser may provide notice and an opportunity to respond. Provisions that by their nature should survive termination will continue to apply.</p>
        </S>

        <S n={21} title="Governing Law">
          <p>These Terms are governed by the laws of the Province of Ontario and the federal laws of Canada applicable in Ontario, without regard to conflict-of-law principles.</p>
          <p>Subject to any mandatory consumer-protection rights, disputes will be brought before the courts located in Ontario, Canada. Nothing in these Terms limits rights available under mandatory laws in your province, territory, state or country of residence.</p>
        </S>

        <S n={22} title="Changes to These Terms">
          <p>We may update these Terms to reflect changes to the Platform, our practices or applicable requirements. The updated version will be posted with a revised "Last Updated" date. Where required, we will provide additional notice or obtain renewed agreement.</p>
        </S>

        <S n={23} title="Severability">
          <p>If any provision is found unenforceable, it will be limited or removed only to the minimum extent necessary. The remaining provisions will continue in effect.</p>
        </S>

        <S n={24} title="Entire Agreement">
          <p>These Terms, our Privacy Policy, applicable Cookie Policy, Campaign Official Rules and any separately signed agreement constitute the agreement applicable to your use of the Platform.</p>
        </S>

        <S n={25} title="Contact">
          <p>Questions about these Terms may be directed to:</p>
          <div className="bg-neutral-50 border border-neutral-200 rounded-xl p-5 space-y-1">
            <p><span className="font-semibold text-neutral-800">Organization:</span> Spinser Inc.</p>
            <p><span className="font-semibold text-neutral-800">Email:</span> <a href="mailto:info@tryspinser.com" className="text-[#FF3B30] hover:underline">info@tryspinser.com</a></p>
            <p><span className="font-semibold text-neutral-800">Website:</span> tryspinser.com</p>
          </div>
        </S>

        <div className="border-t border-neutral-200 pt-8 mt-4">
          <Link to="/" data-testid="terms-back-home-link" className="text-[#FF3B30] font-medium hover:underline">&larr; Back to Home</Link>
        </div>
      </div>
    </div>
  );
}
